import { useRef, useState } from "react";
import EnhancedFilter from "../EnhancedFilter";
import ReusableSelect from "../general/ReusableSelect";
import FilterChips from "./FilterChips";
import FilterDrawer from "./FilterDrawer";

// Filters live in a right-hand drawer and are applied together with "Apply
// filters" (agreed for phase 2). Both hooks drive the page's existing filter
// handlers — they only decide *when* those handlers run.

const isSet = (value) =>
  Array.isArray(value) ? value.length > 0 : String(value ?? "").length > 0;

// Same clearing rule as Filter.jsx's reset()
const clearFiltered = (filtered) => {
  const next = window.structuredClone(filtered);
  next.forEach((f) => (Array.isArray(f.state) ? (f.state = []) : (f.state = "")));
  return next;
};

/**
 * Pages that use components/Filter.jsx: `filtered` state + onChangeFiltered().
 * `filterOptions` is the per-filter options array the page already builds
 * with makeOptions(), in the same order as `filtered`.
 */
export function useLegacyFilters({ filtered, filterOptions, onChangeFiltered }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(filtered);

  const optionsAt = (i) =>
    (filterOptions?.[i] || []).map((o) => ({ value: o.id, option: o.option }));

  const openDrawer = () => {
    setDraft(window.structuredClone(filtered));
    setOpen(true);
  };
  const close = () => setOpen(false);

  // Same update rule as Dropdown.filtering() in Filter.jsx, applied to the draft
  const setDraftValue = (header, value) => {
    const next = window.structuredClone(draft);
    const index = next.findIndex((f) => f.header === header);
    if (typeof next[index].state === "string") next[index].state = value;
    else next[index].state = typeof value === "string" ? value.split(",") : value;
    setDraft(next);
  };

  const apply = () => {
    onChangeFiltered(draft);
    close();
  };
  const reset = () => {
    const next = clearFiltered(filtered);
    setDraft(next);
    onChangeFiltered(next);
    close();
  };

  const chips = filtered
    .map((f, i) => {
      if (!isSet(f.state)) return null;
      const name = (v) => optionsAt(i).find((o) => o.value === v)?.option ?? v;
      return {
        key: f.header,
        label: f.label,
        value: Array.isArray(f.state) ? f.state.map(name).join(", ") : name(f.state),
        onRemove: () => {
          const next = window.structuredClone(filtered);
          next[i].state = Array.isArray(next[i].state) ? [] : "";
          onChangeFiltered(next);
        },
      };
    })
    .filter(Boolean);

  return {
    openDrawer,
    isOpen: open,
    count: chips.length,
    chips: <FilterChips chips={chips} onClearAll={reset} />,
    drawer: (
      <FilterDrawer open={open} onClose={close} onReset={reset} onApply={apply}>
        {draft.map(({ label, state, header }, i) => (
          <ReusableSelect
            key={header}
            compact
            type={typeof state === "object" ? "multipleFilter" : "filter"}
            label={label}
            state={state}
            setState={setDraftValue}
            target={header}
            options={optionsAt(i)}
          />
        ))}
      </FilterDrawer>
    ),
  };
}

/**
 * Pages that use components/general/FiltersWrapper.jsx: each filter is
 * { key, label, endpoint, value, onChange, setValue }. Dropdown options are
 * narrowed by the other *draft* values through the page's adjustEndpoint
 * (third argument), so cascading still works before Apply.
 */
export function useEnhancedFilters({ filters, adjustEndpoint }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const labels = useRef({});

  const applied = () => Object.fromEntries(filters.map((f) => [f.key, f.value ?? ""]));
  const openDrawer = () => {
    setDraft(applied());
    setOpen(true);
  };
  const close = () => setOpen(false);

  // One call per changed filter; React batches them into a single fetch
  const apply = () => {
    filters.forEach((f) => {
      if (draft[f.key] !== (f.value ?? "")) f.onChange(draft[f.key]);
    });
    close();
  };
  // Same as FiltersWrapper's resetFilters()
  const reset = () => {
    filters.forEach((f) => f.setValue(""));
    setDraft(Object.fromEntries(filters.map((f) => [f.key, ""])));
    close();
  };

  const chips = filters
    .filter((f) => isSet(f.value))
    .map((f) => ({
      key: f.key,
      label: f.label,
      value: labels.current[`${f.key}:${f.value}`] ?? f.value,
      onRemove: () => f.setValue(""),
    }));

  return {
    openDrawer,
    isOpen: open,
    count: chips.length,
    chips: <FilterChips chips={chips} onClearAll={reset} />,
    drawer: (
      <FilterDrawer open={open} onClose={close} onReset={reset} onApply={apply}>
        {filters.map((f) => (
          <EnhancedFilter
            key={f.key}
            compact
            label={f.label}
            endpoint={adjustEndpoint(f.endpoint, draft[f.key], draft)}
            value={draft[f.key] ?? ""}
            onChange={(value) => setDraft((d) => ({ ...d, [f.key]: value }))}
            onSelectLabel={(value, name) => {
              labels.current[`${f.key}:${value}`] = name;
            }}
          />
        ))}
      </FilterDrawer>
    ),
  };
}
